import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TrashIcon } from '@radix-ui/react-icons';
import './Templates.css';
import { GridData } from '../services/firestoreService';
import { useUserStorage } from '../services/userDataStorage';
import ConfirmDialog from './ConfirmDialog';

interface TemplatesProps {
  onSidebarRefresh?: () => void;
}

interface TemplateData extends GridData {
  description?: string;
  category?: string;
  isPublic?: boolean;
}

const Templates: React.FC<TemplatesProps> = ({ onSidebarRefresh }) => {
  const navigate = useNavigate();
  const [generalTemplates, setGeneralTemplates] = useState<TemplateData[]>([]);
  const [myTemplates, setMyTemplates] = useState<TemplateData[]>([]);
  const [generalPreviews, setGeneralPreviews] = useState<{ [key: string]: GridData }>({});
  const [myTemplatePreviews, setMyTemplatePreviews] = useState<{ [key: string]: GridData }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading templates...');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const userStorage = useUserStorage();
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStorage?.isFirebaseReady]);

  const loadTemplates = async () => {
    try {
      if (!userStorage?.isFirebaseReady) {
        // Keep loading state true if Firebase is not ready yet
        return;
      }

      setIsLoading(true);
      setLoadingMessage('Loading templates...');
      
      // Load templates metadata quickly (optimized calls)
      const [generalTemplatesData, myTemplatesData] = await Promise.all([
        userStorage.getGeneralTemplates(),
        userStorage.getMyTemplates()
      ]);
      
      // Sort templates by lastModified (most recent first), then by created
      const sortTemplates = (templates: TemplateData[]) => {
        return templates.sort((a, b) => {
          // First sort by lastModified (most recent first)
          const aModified = a.lastModified || a.created || 0;
          const bModified = b.lastModified || b.created || 0;
          
          if (aModified !== bModified) {
            return bModified - aModified; // Descending order (newest first)
          }
          
          // If lastModified is the same, sort by created (most recent first)
          const aCreated = a.created || 0;
          const bCreated = b.created || 0;
          return bCreated - aCreated; // Descending order (newest first)
        });
      };
      
      const sortedGeneralTemplates = sortTemplates([...generalTemplatesData]);
      const sortedMyTemplates = sortTemplates([...myTemplatesData]);
      
      setGeneralTemplates(sortedGeneralTemplates);
      setMyTemplates(sortedMyTemplates);
      setTemplatesLoaded(true);
      setIsLoading(false);

      // Load preview data in background without blocking UI
      if (sortedGeneralTemplates.length > 0 || sortedMyTemplates.length > 0) {
        setBackgroundLoading(true);
        loadPreviewsInBackground(sortedGeneralTemplates, sortedMyTemplates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setIsLoading(false);
      setBackgroundLoading(false);
    }
  };

  const loadPreviewsInBackground = async (generalTemplatesData: TemplateData[], myTemplatesData: TemplateData[]) => {
    try {
      if (!userStorage) {
        setBackgroundLoading(false);
        return;
      }

      const generalPreviews: { [key: string]: GridData } = {};
      const myPreviews: { [key: string]: GridData } = {};
      
      // Load previews with limited concurrency to avoid overwhelming Firestore
      const BATCH_SIZE = 3; // Process 3 previews at a time
      
      // Process general templates in batches
      for (let i = 0; i < generalTemplatesData.length; i += BATCH_SIZE) {
        const batch = generalTemplatesData.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (template) => {
          try {
            if (!userStorage) return;
            const previewData = await userStorage.loadTemplatePreview(template.id, 'general');
            if (previewData) {
              generalPreviews[template.id] = previewData;
              // Update UI incrementally
              setGeneralPreviews(prev => ({ ...prev, [template.id]: previewData }));
            }
          } catch (error) {
            console.error(`Error loading preview for general template ${template.id}:`, error);
          }
        });
        
        await Promise.all(batchPromises);
        
        // Small delay between batches to not overwhelm Firestore
        if (i + BATCH_SIZE < generalTemplatesData.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Process personal templates in batches
      for (let i = 0; i < myTemplatesData.length; i += BATCH_SIZE) {
        const batch = myTemplatesData.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (template) => {
          try {
            if (!userStorage) return;
            const previewData = await userStorage.loadTemplatePreview(template.id, 'personal');
            if (previewData) {
              myPreviews[template.id] = previewData;
              // Update UI incrementally
              setMyTemplatePreviews(prev => ({ ...prev, [template.id]: previewData }));
            }
          } catch (error) {
            console.error(`Error loading preview for my template ${template.id}:`, error);
          }
        });
        
        await Promise.all(batchPromises);
        
        // Small delay between batches
        if (i + BATCH_SIZE < myTemplatesData.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setBackgroundLoading(false);
    } catch (error) {
      console.error('Error loading template previews:', error);
      setBackgroundLoading(false);
    }
  };

  const handleUseTemplate = async (template: TemplateData, isGeneral: boolean) => {
    try {
      if (!userStorage) return;
      
      setIsLoading(true);
      setLoadingMessage('Creating spreadsheet from template...');
      
      // Create a new grid based on the template
      const newGrid = await userStorage.createGridFromTemplate(template.id, isGeneral ? 'general' : 'personal');
      if (onSidebarRefresh) onSidebarRefresh();
      navigate(`/sheets/${newGrid.id}`);
    } catch (error) {
      console.error('Error creating grid from template:', error);
      toast.error('Failed to create spreadsheet from template. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDeleteMyTemplate = async (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      onConfirm: () => confirmDeleteTemplate(templateId, 'personal')
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteGeneralTemplate = async (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Global Template',
      message: 'Are you sure you want to delete this global template? This action cannot be undone and will affect all users.',
      onConfirm: () => confirmDeleteTemplate(templateId, 'general')
    });
  };

  const confirmDeleteTemplate = async (templateId: string, type: 'personal' | 'general') => {
    try {
      if (!userStorage) return;
      
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      setIsLoading(true);
      setLoadingMessage('Deleting template...');
      
      if (type === 'personal') {
        await userStorage.deleteMyTemplate(templateId);
        
        // Optimistic update - remove from UI immediately (maintains sort order)
        setMyTemplates(prev => prev.filter(t => t.id !== templateId));
        setMyTemplatePreviews(prev => {
          const updated = { ...prev };
          delete updated[templateId];
          return updated;
        });
      } else {
        await userStorage.deleteGlobalTemplate(templateId);
        
        // Optimistic update - remove from UI immediately (maintains sort order)
        setGeneralTemplates(prev => prev.filter(t => t.id !== templateId));
        setGeneralPreviews(prev => {
          const updated = { ...prev };
          delete updated[templateId];
          return updated;
        });
      }
      
      toast.success('Template deleted successfully');
      setIsLoading(false);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template. Please try again.');
      // Reload templates on error (will maintain sorting)
      await loadTemplates();
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return "Created just now";
    } else if (diffMinutes < 5) {
      return `Created ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffMinutes < 60) {
      return `Created ${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return `Created ${diffHours}h ago`;
    } else if (diffDays === 1) {
      return "Created yesterday";
    } else if (diffDays < 7) {
      return `Created ${diffDays}d ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Created ${weeks}w ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `Created ${months}mo ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `Created ${years}y ago`;
    }
  };

  const getColumnHeader = (col: any, index: number): string => {
    if (index === 0) {
      return '#';
    }
    return col.name || '';
  };

  if (isLoading) {
    return (
      <div className="templates-container">
        <div className="templates-header">
          <h1>Templates</h1>
        </div>
        <div className="loading-container">
          <div className="spinner" />
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-container">
      <div className="templates-header">
        <h1>Templates</h1>
        {backgroundLoading && (
          <div className="background-loading-indicator">
            <div className="small-spinner"></div>
            <span>Loading previews...</span>
          </div>
        )}
      </div>
      
      {/* General Templates Section */}
      <div className="templates-section">
        <div className="section-header">
          <h2>Global Templates</h2>
          <p>Ready-to-use templates for common use cases</p>
        </div>
        
        <div className="templates-grid">
          {generalTemplates.length === 0 ? (
            <div className="empty-state">
              <h3>No general templates available</h3>
              <p>Check back later for new templates!</p>
            </div>
          ) : (
            generalTemplates.map((template) => {
              const preview = generalPreviews[template.id];
              const hasPreview = !!preview;
              
              return (
                <div 
                  key={template.id}
                  className="template-card global-template"
                  onClick={() => handleUseTemplate(template, true)}
                >
                  <div className="card-header">
                    <h3 className="template-title">{template.title}</h3>
                    {/* <button 
                      className="delete-btn"
                      onClick={(e) => handleDeleteGeneralTemplate(template.id, e)}
                      title="Delete global template"
                    >
                      <TrashIcon className="delete-icon" />
                    </button> */}
                  </div>
                  
                  {template.description && (
                    <div className="template-description">
                      <p>{template.description}</p>
                    </div>
                  )}
                  
                  <div className="template-preview">
                    {hasPreview ? (
                      <table className="preview-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {preview.columnConfigs.slice(0, 5).map((col, index) => (
                              <th key={col.id}>{getColumnHeader(col, index + 1)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.gridData.slice(0, 8).map((row, rowIndex) => (
                            <tr key={row.id}>
                              <td>{row.id}</td>
                              {preview.columnConfigs.slice(0, 5).map((col) => (
                                <td key={col.id}>
                                  {String(row[col.id] || '').slice(0, 15)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : backgroundLoading ? (
                      <div className="loading-preview">
                        <div className="preview-spinner"></div>
                        <p>Loading preview...</p>
                      </div>
                    ) : (
                      <div className="preview-placeholder">
                        <p>Preview will load shortly...</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="card-footer">
                    <span className="template-category">
                      {template.category || 'General'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* My Templates Section */}
      <div className="templates-section">
        <div className="section-header">
          <h2>My Templates</h2>
          <p>Templates you've created from your spreadsheets</p>
        </div>
        
        <div className="templates-grid">
          {myTemplates.length === 0 ? (
            <div className="empty-state">
              <h3>No personal templates yet</h3>
              <p>Create templates from your spreadsheets using the "Create Template" button in the sheet header</p>
            </div>
          ) : (
            myTemplates.map((template) => {
              const preview = myTemplatePreviews[template.id];
              const hasPreview = !!preview;
              
              return (
                <div 
                  key={template.id}
                  className="template-card"
                  onClick={() => handleUseTemplate(template, false)}
                >
                  <div className="card-header">
                    <h3 className="template-title">{template.title}</h3>
                    <button 
                      className="delete-btn"
                      onClick={(e) => handleDeleteMyTemplate(template.id, e)}
                      title="Delete template"
                    >
                      <TrashIcon className="delete-icon" />
                    </button>
                  </div>
                  
                  {template.description && (
                    <div className="template-description">
                      <p>{template.description}</p>
                    </div>
                  )}
                  
                  <div className="template-preview">
                    {hasPreview ? (
                      <table className="preview-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {preview.columnConfigs.slice(0, 5).map((col, index) => (
                              <th key={col.id}>{getColumnHeader(col, index + 1)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.gridData.slice(0, 8).map((row, rowIndex) => (
                            <tr key={row.id}>
                              <td>{row.id}</td>
                              {preview.columnConfigs.slice(0, 5).map((col) => (
                                <td key={col.id}>
                                  {String(row[col.id] || '').slice(0, 15)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : backgroundLoading ? (
                      <div className="loading-preview">
                        <div className="preview-spinner"></div>
                        <p>Loading preview...</p>
                      </div>
                    ) : (
                      <div className="preview-placeholder">
                        <p>Preview will load shortly...</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="card-footer">
                    <span className="last-modified">
                      {formatDate(template.lastModified)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default Templates; 